"use client";

import type {ApexOptions} from "apexcharts";
import dynamic from "next/dynamic";
import {useMemo} from "react";
import {FiZap} from "react-icons/fi";

const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

type SosStatusChartProps = {
  categories: string[];
  activeSeries: number[];
  resolvedSeries: number[];
  falseAlarmSeries: number[];
};

export const SosStatusChart = ({
  categories,
  activeSeries,
  resolvedSeries,
  falseAlarmSeries
}: SosStatusChartProps) => {
  const series = useMemo(
    () => [
      {
        name: "Active",
        data: activeSeries
      },
      {
        name: "Resolved",
        data: resolvedSeries
      },
      {
        name: "False Alarm",
        data: falseAlarmSeries
      }
    ],
    [activeSeries, falseAlarmSeries, resolvedSeries]
  );

  const maxValue = useMemo(() => {
    const totals = categories.map(
      (_, index) =>
        (activeSeries[index] ?? 0) +
        (resolvedSeries[index] ?? 0) +
        (falseAlarmSeries[index] ?? 0)
    );
    return Math.max(3, ...totals);
  }, [activeSeries, categories, falseAlarmSeries, resolvedSeries]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: {show: false},
        stacked: true,
        sparkline: {enabled: false}
      },
      colors: ["#f87171", "#4ade80", "#9ca3af"],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          borderRadiusApplication: "end",
          columnWidth: "42%"
        }
      },
      dataLabels: {
        enabled: false
      },
      grid: {
        borderColor: "#e2e8f0",
        strokeDashArray: 0,
        xaxis: {
          lines: {show: false}
        },
        yaxis: {
          lines: {show: true}
        },
        padding: {
          left: 12,
          right: 8,
          top: 4,
          bottom: 0
        }
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: "#64748b",
            fontSize: "11px",
            fontWeight: 500
          },
          rotate: 0
        },
        axisBorder: {show: false},
        axisTicks: {show: false}
      },
      yaxis: {
        min: 0,
        max: maxValue,
        tickAmount: Math.min(maxValue, 3),
        labels: {
          formatter: value => `${Math.round(value)} cases`,
          style: {
            colors: "#64748b",
            fontSize: "11px"
          }
        }
      },
      legend: {show: false},
      tooltip: {
        theme: "light",
        y: {
          formatter: value => `${value} cases`
        }
      }
    }),
    [categories, maxValue]
  );

  return (
    <div className="min-w-0 flex-1 rounded-[20px] border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
      <div className="mb-1 flex items-center justify-center gap-2 text-slate-900">
        <FiZap className="h-3 w-3 text-blue-600" />
        <div className="text-[10px] font-semibold">Trends</div>
      </div>
      <Chart type="bar" options={options} series={series} height={105} />
    </div>
  );
};
